import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CreateWidgetBody, UpdateWidgetBody, api } from "@/lib/api";
import { queryKeys } from "@/features/admin/features/pages-management";
import { useInvalidateSelectedDashboard } from "@/features/admin/hooks/use-invalidate-selected-dashboard";

export const useCreateWidgetMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentSelectedDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({ pageId, data }: { pageId: string; data: CreateWidgetBody }) =>
      api.widgets.createWidget(pageId, data),
    onSuccess: (_, { pageId }) => {
      invalidateCurrentSelectedDashboard();
      queryClient.invalidateQueries({ queryKey: queryKeys.page(pageId) });
    },
  });
};

export const useDeleteWidgetMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentSelectedDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({ widgetId }: { widgetId: string; pageId: string }) =>
      api.widgets.deleteWidget(widgetId),
    onSuccess: (_, { pageId }) => {
      invalidateCurrentSelectedDashboard();
      queryClient.invalidateQueries({ queryKey: queryKeys.page(pageId) });
    },
  });
};

export const useDeleteAllWidgetsMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentSelectedDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({ widgetContainerId }: { pageId: string; widgetContainerId: string }) =>
      api.widgets.deleteWidgetContainer(widgetContainerId),
    onSuccess: (_, { pageId }) => {
      invalidateCurrentSelectedDashboard();
      queryClient.invalidateQueries({ queryKey: queryKeys.page(pageId) });
    },
  });
};

export const useUpdateWidgetMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentSelectedDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      widgetId,
      data,
    }: {
      widgetId: string;
      pageId: string;
      data: UpdateWidgetBody;
    }) => api.widgets.updateWidget(widgetId, data),
    onSuccess: (_, { pageId }) => {
      invalidateCurrentSelectedDashboard();
      queryClient.invalidateQueries({ queryKey: queryKeys.page(pageId) });
    },
  });
};

export const useReorderWidgetsMutation = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentSelectedDashboard = useInvalidateSelectedDashboard();

  return useMutation({
    mutationFn: ({
      widgetId,
      data,
    }: {
      widgetId: string;
      data: string[];
      pageId: string;
    }) => {
      return api.widgets.reorderWidgetsForWidgetContainer(widgetId, data);
    },
    onSuccess: (_, { pageId }) => {
      invalidateCurrentSelectedDashboard();
      queryClient.invalidateQueries({ queryKey: queryKeys.page(pageId) });
    },
  });
};
